import React, { useState } from "react";

import {
  Table as MaterialUiTable,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableFooter,
  TablePagination,
  Collapse,
  IconButton
} from "@material-ui/core";

import { useI18n } from "../../../hooks/useI18n";

import KeyboardArrowUpIcon from "@material-ui/icons/KeyboardArrowUp";
import KeyboardArrowDownIcon from "@material-ui/icons/KeyboardArrowDown";

import { TableProps } from "./types";
import useStyles from "./styles";

function Table<TData>({
  columns,
  data,
  keyExtractor,
  expandedRowRender,
  paginationOptions
}: TableProps<TData>) {
  const { t } = useI18n();
  const styles = useStyles();
  const [openRows, setOpenRows] = useState<TData[]>([]);

  const isOpen = (dataRow: TData) => openRows.indexOf(dataRow) > -1;

  const renderDataRow = (dataRow: TData, index: number) => (
    <>
      <TableRow key={keyExtractor(dataRow, index)}>
        {expandedRowRender && (
          <TableCell>
            <IconButton
              aria-label={t("common.tooltip.expandRow")}
              size="small"
              onClick={() => {
                if (isOpen(dataRow)) {
                  setOpenRows(openRows.filter(r => r !== dataRow));
                } else {
                  setOpenRows(openRows.concat(dataRow));
                }
              }}
            >
              {isOpen(dataRow) ? (
                <KeyboardArrowUpIcon />
              ) : (
                <KeyboardArrowDownIcon />
              )}
            </IconButton>
          </TableCell>
        )}
        {columns.map(column => (
          <TableCell key={`${column.name}_${keyExtractor(dataRow, index)}`}>
            {column.renderItem(dataRow)}
          </TableCell>
        ))}
      </TableRow>

      {expandedRowRender !== undefined && (
        <TableRow>
          <TableCell
            colSpan={columns.length + 1}
            className={styles.expandedRow}
          >
            <Collapse in={isOpen(dataRow)} unmountOnExit>
              {expandedRowRender(dataRow)}
            </Collapse>
          </TableCell>
        </TableRow>
      )}
    </>
  );

  return (
    <MaterialUiTable>
      <TableHead>
        <TableRow>
          {expandedRowRender && <TableCell />}
          {columns.map(column => (
            <TableCell key={column.name}>{column.name}</TableCell>
          ))}
        </TableRow>
      </TableHead>

      <TableBody>
        {data.map((dataRow, index) => renderDataRow(dataRow, index))}
      </TableBody>
      {paginationOptions && (
        <TableFooter>
          <TableRow>
            <TablePagination
              {...paginationOptions}
              rowsPerPageOptions={paginationOptions.rowsPerPageOptions ?? []}
            />
          </TableRow>
        </TableFooter>
      )}
    </MaterialUiTable>
  );
}

export default Table;
